/**
 * useAuth Hook
 * Provides easy access to authentication state and methods
 */

import { useCallback, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '@app/store';
import { setCredentials, updateAccessToken, logout, checkAuthStatus } from '../store/authSlice';
import { Result, success, failure } from '@shared/types/result.types';
import { handleApiError } from '@shared/utils/error-handler';
import {
  useAdminLoginMutation,
  useSendOtpMutation,
  useVerifyOtpMutation,
  useCreateUserMutation,
  useRefreshTokenMutation,
  useLogoutMutation,
} from '../api/authApi';
import type {
  AdminLoginRequest,
  SendOtpRequest,
  VerifyOtpRequest,
  CreateUserRequest,
} from '../api/authApi';

export const useAuth = () => {
  const dispatch = useDispatch();
  const auth = useSelector((state: RootState) => state.auth);
  const [isInitializing, setIsInitializing] = useState(true);

  // Mutations
  const [adminLogin, { isLoading: isAdminLoginLoading }] = useAdminLoginMutation();
  const [sendOtp, { isLoading: isSendOtpLoading }] = useSendOtpMutation();
  const [verifyOtp, { isLoading: isVerifyOtpLoading }] = useVerifyOtpMutation();
  const [createUser, { isLoading: isCreateUserLoading }] = useCreateUserMutation();
  const [refreshToken, { isLoading: isRefreshLoading }] = useRefreshTokenMutation();
  const [logoutMutation, { isLoading: isLogoutLoading }] = useLogoutMutation();

  // Handle initialization
  useEffect(() => {
    // Mark initialization as complete once we've checked auth state from storage
    setIsInitializing(false);
  }, []);

  /**
   * Admin login handler
   */
  const handleAdminLogin = useCallback(
    async (credentials: AdminLoginRequest): Promise<Result<any>> => {
      try {
        const result = await adminLogin(credentials).unwrap();
        dispatch(
          setCredentials({
            accessToken: result.accessToken,
            refreshToken: result.refreshToken,
            accessExpiryMillis: result.accessExpiryMillis,
          })
        );
        return success(result);
      } catch (error) {
        return failure(handleApiError(error));
      }
    },
    [adminLogin, dispatch]
  );

  /**
   * Send OTP handler
   */
  const handleSendOtp = useCallback(
    async (data: SendOtpRequest): Promise<Result<any>> => {
      try {
        const result = await sendOtp(data).unwrap();
        return success(result);
      } catch (error) {
        return failure(handleApiError(error));
      }
    },
    [sendOtp]
  );

  /**
   * Verify OTP handler
   */
  const handleVerifyOtp = useCallback(
    async (data: VerifyOtpRequest): Promise<Result<any>> => {
      try {
        const result = await verifyOtp(data).unwrap();
        
        // Only set credentials if user doesn't need registration
        if (!result.requiresRegistration) {
          dispatch(
            setCredentials({
              accessToken: result.accessToken,
              refreshToken: result.refreshToken,
              accessExpiryMillis: result.accessExpiryMillis,
            })
          );
        }
        
        return success(result);
      } catch (error) {
        return failure(handleApiError(error));
      }
    },
    [verifyOtp, dispatch]
  );

  /**
   * Create user handler
   */
  const handleCreateUser = useCallback(
    async (data: CreateUserRequest): Promise<Result<any>> => {
      try {
        const result = await createUser(data).unwrap();
        dispatch(
          setCredentials({
            accessToken: result.accessToken,
            refreshToken: result.refreshToken,
            accessExpiryMillis: result.accessExpiryMillis,
            user: result.user,
          })
        );
        return success(result);
      } catch (error) {
        return failure(handleApiError(error));
      }
    },
    [createUser, dispatch]
  );

  /**
   * Refresh token handler
   */
  const handleRefreshToken = useCallback(async () => {
    try {
      if (!auth.refreshToken) {
        throw new Error('No refresh token available');
      }
      
      const result = await refreshToken({ refreshToken: auth.refreshToken }).unwrap();
      dispatch(
        updateAccessToken({
          accessToken: result.accessToken,
          accessExpiryMillis: result.accessExpiryMillis || Date.now() + 3600000,
        })
      );
      return { success: true, data: result };
    } catch (error) {
      // Clear auth state on refresh failure
      dispatch(logout());
      return { success: false, error };
    }
  }, [auth.refreshToken, refreshToken, dispatch]);

  /**
   * Logout handler
   */
  const handleLogout = useCallback(async () => {
    try {
      await logoutMutation().unwrap();
    } catch (error) {
      // Log error but still clear local state
      console.error('Logout API call failed:', error);
    } finally {
      // Always clear local auth state
      dispatch(logout());
    }
  }, [logoutMutation, dispatch]);

  /**
   * Check authentication status
   */
  const checkAuth = useCallback(() => {
    dispatch(checkAuthStatus());
  }, [dispatch]);

  return {
    // State
    user: auth.user,
    accessToken: auth.accessToken,
    refreshToken: auth.refreshToken,
    isAuthenticated: auth.isAuthenticated,
    tokenExpiry: auth.tokenExpiry,
    
    // Methods
    adminLogin: handleAdminLogin,
    sendOtp: handleSendOtp,
    verifyOtp: handleVerifyOtp,
    createUser: handleCreateUser,
    refreshAccessToken: handleRefreshToken,
    logout: handleLogout,
    checkAuth,
    
    // Loading states
    isLoading:
      isInitializing ||
      isAdminLoginLoading ||
      isSendOtpLoading ||
      isVerifyOtpLoading ||
      isCreateUserLoading ||
      isRefreshLoading ||
      isLogoutLoading,
    isAdminLoginLoading,
    isSendOtpLoading,
    isVerifyOtpLoading,
    isCreateUserLoading,
    isRefreshLoading,
    isLogoutLoading,
  };
};
